import React from 'react';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';
import styled from 'styled-components';
import { connect } from 'react-redux';

import { getTimerTooltipText } from '..';
import Timer from '../../timer';
import Tooltip from '../../tooltip';

import ZanichelliLogo from '../assets/zanichelli-logo.svg';

import ColumnContainer from '../../layouts/components/column-container';
import FluidContainer from '../../layouts/components/fluid-container';

import Skeleton from '../../skeleton';
import { Show } from '../../show';
import PauseButton from '../../pause-button';
import { useOnlineStatus } from '../../../lib/hooks';

export const Logo = styled.img`
    width: 100%;
    ${(props) => props.isLogoClickable && 'cursor: pointer;'}
`;

const BookAndTitleContainer = styled.div`
    display: flex;
    justify-content: center;
    flex-direction: column;
    overflow: hidden;
`;

export const Authors = styled.span`
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.16px;
    font-weight: normal;

    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
`;

export const Book = styled.span`
    font-size: 20px;
    line-height: 28px;
    letter-spacing: 0;
    font-weight: 600;

    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
`;

const HomeworkDetailsContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const TypeAndNumberOfExercisesContainer = styled.div`
    display: flex;
    align-items: center;
    height: 28px;
    border-radius: 14px;
    padding: 0 12px;
    background-color: var(--color-white);
    border: 1px solid var(--gray200);
    margin-left: 24px;
    box-sizing: border-box;
`;

const Type = styled.span`
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.32px;
    font-weight: 600;
    margin-right: 4px;
`;

export const NumberOfExercises = styled.span`
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.32px;
    font-weight: normal;
    white-space: nowrap;
`;

export const TimerTooltipContainer = styled.div`
    position: relative;

    &:hover > .TooltipWrapper {
        display: block;
    }

    .TooltipWrapper {
        display: none;
    }

    ${(props) => props.theme.mediaQueries.tablet} {
        margin-left: 24px;
    }
`;

const TimerTooltip = styled(Tooltip)`
    white-space: nowrap;
    color: var(--color-white);
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.16px;
    line-height: 20px;
    text-transform: uppercase;
`;

const TimerWrapper = styled.div`
    display: flex;
    align-items: center;
`;

const DesktopHeader = ({
    applicationStatus = {},
    authors,
    book,
    finished,
    homeworkStatus = {},
    homeworkTime,
    isLoading,
    numberOfExercises = 0,
    paused,
    profile,
    redirect,
    showModal,
    started,
    status,
    terminateHomework,
    type,
    updateStatusError,
}) => {
    const onlineStatus = useOnlineStatus();
    const isLogoClickable = !isLoading && !profile.isEmbedded;

    const exercisesNumberLabel = () => {
        if (profile.sections) return 'quiz';
        else return numberOfExercises === 1 ? 'esercizio' : 'esercizi';
    };

    return (
        <ColumnContainer height={80} backgroundColor="surface-grey-050" boxShadow="shadow-01dp">
            <ColumnContainer width={[0, 2 / 8, 2 / 12]}>
                <Logo src={ZanichelliLogo} isLogoClickable={isLogoClickable} onClick={isLogoClickable ? redirect : null} />
            </ColumnContainer>

            <ColumnContainer width={[0, 0, 1 / 12]} />

            <ColumnContainer width={[1, 6 / 8, 8 / 12]}>
                <FluidContainer height={80} alignItems="center" justifyContent="space-between" paddingX={['mobile', 'tablet', 'desktop']}>
                    {applicationStatus.completed && !homeworkStatus.error && (
                        <>
                            <Skeleton isLoading={isLoading} items={[{ width: 30 }, { width: 50 }]}>
                                <BookAndTitleContainer>
                                    {book && (
                                        <>
                                            <Authors
                                                dangerouslySetInnerHTML={{
                                                    __html: DOMPurify.sanitize(authors),
                                                }}
                                            />
                                            <Book>{book}</Book>
                                        </>
                                    )}
                                </BookAndTitleContainer>
                            </Skeleton>

                            <HomeworkDetailsContainer>
                                <Skeleton isLoading={isLoading} items={[{ height: 'regular' }]} width={80}>
                                    <TypeAndNumberOfExercisesContainer>
                                        <Type>{type}</Type>
                                        <NumberOfExercises>{`${numberOfExercises} ${exercisesNumberLabel()}`}</NumberOfExercises>
                                    </TypeAndNumberOfExercisesContainer>
                                </Skeleton>

                                {(isLoading || (status && Number.isInteger(status.remainingTime))) && (
                                    <TimerWrapper>
                                        <TimerTooltipContainer>
                                            <Skeleton isLoading={isLoading} items={[{ height: 'regular' }]} width={80}>
                                                <Timer terminateHomework={terminateHomework} />
                                                <TimerTooltip content={getTimerTooltipText(status, homeworkTime)} top="-5px" right="32px" />
                                            </Skeleton>
                                        </TimerTooltipContainer>
                                    </TimerWrapper>
                                )}
                                <Show when={profile.pauseTest && started && !finished && !paused}>
                                    <PauseButton disabled={!onlineStatus || updateStatusError} showModal={showModal} />
                                </Show>
                            </HomeworkDetailsContainer>
                        </>
                    )}
                </FluidContainer>
            </ColumnContainer>

            <ColumnContainer width={[0, 0, 1 / 12]} />
        </ColumnContainer>
    );
};

DesktopHeader.propTypes = {
    applicationStatus: PropTypes.object,
    authors: PropTypes.string,
    book: PropTypes.string,
    finished: PropTypes.bool,
    homeworkStatus: PropTypes.object,
    homeworkTime: PropTypes.number,
    isLoading: PropTypes.bool,
    numberOfExercises: PropTypes.number,
    paused: PropTypes.bool,
    profile: PropTypes.object,
    showModal: PropTypes.func,
    started: PropTypes.bool,
    status: PropTypes.object,
    type: PropTypes.string,
    updateStatusError: PropTypes.bool,
    redirect: PropTypes.func,
    terminateHomework: PropTypes.func,
};

const stateToProps = (state) => {
    return {
        finished: state.status.finished,
        paused: state.status.paused,
        profile: state.application,
        started: state.status.started,
        status: state.status,
        updateStatusError: !!state.status.httpStatus.errorbody,
    };
};

export default connect(stateToProps)(DesktopHeader);
